// ═══════════════════════════════════════════════════════
// GameDataService — loads and validates all JSON configs at startup
// No Phaser imports. Singleton pattern.
// ═══════════════════════════════════════════════════════
import type { TowerCard, UnitCard, Hero, MapConfig, MatchConfig, Player, Deck } from '../interfaces/index'
import {
  TowersFileSchema,
  UnitsFileSchema,
  HeroesFileSchema,
  MapConfigSchema,
  CardSkillTreesFileSchema,
} from '../schemas/dataSchemas'
import { z } from 'zod'
import { LocalBotTransport } from '../adapters/LocalBotTransport'
import { CardProgressionSystem } from '../engine/CardProgressionSystem.js'

// JSON imports — Vite resolves these natively (used by the non-Phaser load() path)
import towersRaw from '../data/towers.json'
import unitsRaw from '../data/units.json'
import heroesRaw from '../data/heroes.json'
import elementMatrixRaw from '../data/element-matrix.json'
import mapVulkanRaw from '../data/maps/map-vulkan.json'
import cardSkillTreesRaw from '../data/card-skill-trees.json'

// Element matrix has metadata keys "_comment" and "_version" — strip them
const ElementMatrixSchema = z.record(z.string(), z.record(z.string(), z.number()))

function stripMatrixMeta(
  raw: Record<string, unknown>
): Record<string, Record<string, number>> {
  const stripped: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(raw)) {
    if (!k.startsWith('_')) stripped[k] = v
  }
  return stripped as Record<string, Record<string, number>>
}

export class GameDataService {
  private static _instance: GameDataService | null = null

  towers: TowerCard[] = []
  units: UnitCard[] = []
  heroes: Hero[] = []
  elementMatrix: Record<string, Record<string, number>> = {}
  maps: MapConfig[] = []

  private _loaded = false

  private constructor() {}

  static getInstance(): GameDataService {
    if (!GameDataService._instance) {
      GameDataService._instance = new GameDataService()
    }
    return GameDataService._instance
  }

  /**
   * Called by BootScene after Phaser has loaded JSON files into its cache.
   * Runs Zod validation on the already-fetched raw objects.
   */
  loadFromCache(
    towersRawArg: unknown,
    unitsRawArg: unknown,
    heroesRawArg: unknown,
    elementMatrixRawArg: unknown,
    mapsRawArgs: unknown[],
    cardSkillTreesRawArg?: unknown,
  ): void {
    if (this._loaded) return
    this._validate(towersRawArg, unitsRawArg, heroesRawArg, elementMatrixRawArg, mapsRawArgs)
    this._loadSkillTrees(cardSkillTreesRawArg ?? cardSkillTreesRaw)
    this._loaded = true
  }

  /**
   * Standalone load path for non-Phaser contexts (unit tests, CLI tools).
   * Imports JSON directly via Vite static imports and runs Zod validation.
   */
  load(): void {
    if (this._loaded) return
    this._validate(towersRaw, unitsRaw, heroesRaw, elementMatrixRaw, [mapVulkanRaw])
    this._loadSkillTrees(cardSkillTreesRaw)
    this._loaded = true
  }

  private _validate(
    towersRawArg: unknown,
    unitsRawArg: unknown,
    heroesRawArg: unknown,
    elementMatrixRawArg: unknown,
    mapsRawArgs: unknown[]
  ): void {
    const towersResult = TowersFileSchema.safeParse(towersRawArg)
    if (!towersResult.success) {
      throw new Error(`[GameDataService] towers.json validation failed:\n${towersResult.error.message}`)
    }
    this.towers = towersResult.data as TowerCard[]

    const unitsResult = UnitsFileSchema.safeParse(unitsRawArg)
    if (!unitsResult.success) {
      throw new Error(`[GameDataService] units.json validation failed:\n${unitsResult.error.message}`)
    }
    this.units = unitsResult.data as UnitCard[]

    const heroesResult = HeroesFileSchema.safeParse(heroesRawArg)
    if (!heroesResult.success) {
      throw new Error(`[GameDataService] heroes.json validation failed:\n${heroesResult.error.message}`)
    }
    this.heroes = heroesResult.data as Hero[]

    const matrixStripped = stripMatrixMeta(elementMatrixRawArg as Record<string, unknown>)
    const matrixResult = ElementMatrixSchema.safeParse(matrixStripped)
    if (!matrixResult.success) {
      throw new Error(`[GameDataService] element-matrix.json validation failed:\n${matrixResult.error.message}`)
    }
    this.elementMatrix = matrixResult.data

    this.maps = []
    for (const mapRaw of mapsRawArgs) {
      if (mapRaw === null || mapRaw === undefined) continue
      const mapResult = MapConfigSchema.safeParse(mapRaw)
      if (!mapResult.success) {
        throw new Error(`[GameDataService] map validation failed:\n${mapResult.error.message}`)
      }
      this.maps.push(mapResult.data as MapConfig)
    }
  }

  private _loadSkillTrees(raw: unknown): void {
    const result = CardSkillTreesFileSchema.safeParse(raw)
    if (!result.success) {
      console.warn(`[GameDataService] card-skill-trees.json validation failed — skill trees disabled:\n${result.error.message}`)
      return
    }
    CardProgressionSystem.getInstance().loadTrees(result.data)
  }

  /** Returns all loaded tower cards. Empty array if not yet loaded. */
  getTowers(): TowerCard[] {
    return this.towers
  }

  /** Returns all loaded unit cards. Empty array if not yet loaded. */
  getUnits(): UnitCard[] {
    return this.units
  }

  /** Returns all loaded heroes. Empty array if not yet loaded. */
  getHeroes(): Hero[] {
    return this.heroes
  }

  getTowerById(id: string): TowerCard | undefined {
    return this.towers.find((t) => t.id === id)
  }

  getUnitById(id: string): UnitCard | undefined {
    return this.units.find((u) => u.id === id)
  }

  getHeroById(id: string): Hero | undefined {
    return this.heroes.find((h) => h.id === id)
  }

  /**
   * Builds a MatchConfig from a saved Deck (from progressionStore).
   * Maps deck.towers (string[]) and deck.units (string[]) to actual card data.
   * Falls back gracefully if cards are not found.
   */
  buildMatchConfigFromDeck(
    player1Id: string,
    deck: Deck,
    mapId: string,
    botId: string = 'bot1'
  ): MatchConfig {
    if (!this._loaded) {
      throw new Error(
        '[GameDataService] Must call load() or loadFromCache() before buildMatchConfigFromDeck()'
      )
    }

    const map = this.maps.find((m) => m.id === mapId) ?? this.maps[0]
    if (map === undefined) {
      throw new Error(`[GameDataService] Map not found: ${mapId}`)
    }

    const hero = this.getHeroById(deck.heroId) ?? this.getHeroById('merchant')
    if (hero === undefined) {
      throw new Error('[GameDataService] No hero found for deck')
    }

    // Resolve tower ids — skip any that don't exist in data
    const resolvedTowerIds = deck.towers.filter(
      (id) => this.getTowerById(id) !== undefined
    )
    const resolvedUnitIds = deck.units.filter(
      (id) => this.getUnitById(id) !== undefined
    )

    // Fall back to starter cards if deck resolves to empty
    const finalTowerIds = resolvedTowerIds.length > 0
      ? resolvedTowerIds
      : ['archer', 'firemage', 'frost', 'lightning']
    const finalUnitIds = resolvedUnitIds.length > 0
      ? resolvedUnitIds
      : ['goblin']

    const playerDeck: Deck = {
      ...deck,
      towers: finalTowerIds,
      units: finalUnitIds,
    }

    const botHero = this.getHeroById('merchant') ?? hero

    const botDeck: Deck = {
      id: `${botId}-starter`,
      name: 'Bot Deck',
      iconElement: 'neutral',
      towers: ['archer', 'firemage', 'frost', 'lightning'],
      units: ['goblin'],
      heroId: 'merchant',
    }

    const player1: Player = {
      id: player1Id,
      name: player1Id,
      deck: playerDeck,
      hero,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
    }

    const player2: Player = {
      id: botId,
      name: botId,
      deck: botDeck,
      hero: botHero,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
      isBot: true,
    }

    const availableUnitIds = this.units.map((u) => u.id)
    const transport = new LocalBotTransport(availableUnitIds)

    const config: MatchConfig = {
      mode: '1v1',
      players: [player1, player2],
      map,
      transport,
      durationSecs: 900,
      localPlayerId: player1Id,
    }

    return config
  }

  /**
   * Creates a default 1v1 MatchConfig with starter decks.
   * Player 1 gets: archer + goblin + merchant hero.
   * Bot gets the same starter setup.
   * Uses LocalBotTransport for the opponent.
   */
  createStarterMatchConfig(
    player1Id: string,
    player2Id: string,
    mapId: string
  ): MatchConfig {
    if (!this._loaded) {
      throw new Error(
        '[GameDataService] Must call load() or loadFromCache() before createStarterMatchConfig()'
      )
    }

    const map = this.maps.find((m) => m.id === mapId)
    if (map === undefined) {
      throw new Error(`[GameDataService] Map not found: ${mapId}`)
    }

    const merchant = this.getHeroById('merchant')
    if (merchant === undefined) {
      throw new Error('[GameDataService] Hero "merchant" not found in heroes.json')
    }

    const starterDeck1: Deck = {
      id: `${player1Id}-starter`,
      name: 'Starter Deck',
      iconElement: 'neutral',
      towers: ['archer', 'firemage', 'frost', 'lightning'],
      units: ['goblin'],
      heroId: 'merchant',
    }

    const starterDeck2: Deck = {
      id: `${player2Id}-starter`,
      name: 'Starter Deck',
      iconElement: 'neutral',
      towers: ['archer', 'firemage', 'frost', 'lightning'],
      units: ['goblin'],
      heroId: 'merchant',
    }

    const player1: Player = {
      id: player1Id,
      name: player1Id,
      deck: starterDeck1,
      hero: merchant,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
    }

    const player2: Player = {
      id: player2Id,
      name: player2Id,
      deck: starterDeck2,
      hero: merchant,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
    }

    const availableUnitIds = this.units.map((u) => u.id)
    const transport = new LocalBotTransport(availableUnitIds)

    const config: MatchConfig = {
      mode: '1v1',
      players: [player1, player2],
      map,
      transport,
      durationSecs: 900,
      localPlayerId: player1Id,
    }

    return config
  }
}
